import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { IBannerResultResponse } from "@/models/ApiRequestResponse/patient";

import { getBannerApi } from "@/utils/apis/patient/tests";
import useIsMobile from "./useIsMobile";
import useIsTablet from "./useIsTablet";
import { bannerStyles } from "@/utils/styles";

import useBannerRedirection from "./useBannerRedirection";

export enum BannerType {
  TEST = "Test Banner",
  MEDICINE = "Medicine Banner",
  HOME = "Home Banner",
  MEDICAL_RECORD = "Medical Record Banner",
}

interface UseBannersResult {
  banners: IBannerResultResponse[];
  loading: boolean;
  bannerList: JSX.Element[];
  error: Error | null;
}

/**
 * @param type The type of banner to fetch
 * @returns banner data, loading state, and rendered banner list
 */
const useBanners = (type: BannerType): UseBannersResult => {
  const [banners, setBanners] = useState<IBannerResultResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const navigate = useNavigate();

  const { bannerRedirectionHandler } = useBannerRedirection();

  const handleBannerClick = (banner: IBannerResultResponse) => {
    const redirectUrl = bannerRedirectionHandler(banner.bannerRedirection);
    navigate(redirectUrl);
  };

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);
        const response = await getBannerApi(type);

        if ("result" in response) {
          setBanners(response.result);
        }
      } catch (err) {
        console.error("Error fetching banners:", err);
        setError(
          err instanceof Error ? err : new Error("Unknown error occurred")
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, [type]);

  const bannerList = banners?.map((banner, index) => (
    <div
      key={`${index}-${banner.action}`}
      style={bannerStyles.wrapper}
      onClick={() => {
        handleBannerClick(banner);
      }}
    >
      <Box
        component="img"
        src={isMobile || isTablet ? banner.appUrl : banner.webUrl}
        alt={`Banner ${index + 1}`}
        sx={{
          height: "13rem",
          width: "100%",
          objectFit: { xs: "contain", md: "cover" },
        }}
      />
    </div>
  ));

  return { banners, loading, bannerList, error };
};

export default useBanners;
